import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { useOktaAuth } from '@okta/okta-react';
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Stable New Relic event emitted when the Common Consumer Card is rendered
 * without a usable JWT from Okta. Referenced by NRQL queries on the
 * "MV — Common Consumer Card Integration Health" dashboard (awsdrst account).
 */
export const CCC_JWT_MISSING_EVENT = 'MV:CCC:JwtTokenMissing';

/**
 * React hook that returns a stable callback for retrieving the Okta JWT used to
 * authenticate calls from a federated Common Consumer Card module.
 *
 * If the token is missing (no Okta context, or Okta returns an empty string),
 * a single `MV:CCC:JwtTokenMissing` page action is emitted per hook instance so
 * we can correlate render/auth failures back to a missing-auth root cause
 * without flooding New Relic with one event per render.
 *
 * @param moduleName Logical name of the federated module consuming the token
 *                   (e.g. `CustomerSidebarModule`). Surfaced as a NR attribute.
 */
export const useCustomerCardJwtToken = (moduleName: string): (() => string) => {
    const oktaContext = useOktaAuth();
    const hasLoggedMissingTokenRef = useRef(false);
    // Sourced for New Relic enrichment so MV:CCC:JwtTokenMissing events on the
    // "MV — Common Consumer Card Integration Health" dashboard can be sliced
    // / drilled-into per dealer (string-cast for stable NRQL FACET grouping).
    const dealerId = useSelector(dealerSelectors.getDealerId);

    return useCallback(() => {
        const token = oktaContext?.oktaAuth.getAccessToken() || '';

        if (!token && !hasLoggedMissingTokenRef.current) {
            hasLoggedMissingTokenRef.current = true;
            logNewRelicPageAction(CCC_JWT_MISSING_EVENT, {
                environment: offerSessionUtils.getEnvironmentType(),
                moduleName,
                dealerId: dealerId != null ? String(dealerId) : ''
            });
        }

        return token;
    }, [oktaContext, moduleName, dealerId]);
};
